import React, { useState } from 'react';
import './FoundingWaitlist.css';

/**
 * FoundingWaitlist — shown when AddressCheck returns status "founding".
 *
 * Props
 *  - address          full address string from googleAddress (shown at top)
 *  - zipcode          ZIP from googleAddress
 *  - onChangeAddress  go back to AddressCheck
 *  - onJoin(data)     your API call. Must RETURN the backend response (waitListResponse):
 *                     { success, alreadyRegistered, customMessage, customer }
 *                     data = { name, phone, unit, address, zipcode, smsConsent, source }
 */

const PERKS = [
  { fig: '$20', title: 'Off your first order', text: 'Applied automatically at checkout.' },
  {
    fig: 'Free',
    small: true,
    title: 'Get FREE Juice for 6 Months!',
    text: 'Order over $30 from participating stores. Valid on every qualifying purchase for 6 months.',
  },
  {
    fig: 'More',
    small: true,
    title: 'Founder-only extras',
    text: 'Early access and member deals at launch.',
  },
];

const formatPhone = (value) => {
  const d = value.replace(/\D/g, '').replace(/^1/, '').slice(0, 10);
  if (d.length > 6) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length > 3) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  if (d.length) return `(${d}`;
  return '';
};

const capitalize = (s = '') => s.charAt(0).toUpperCase() + s.slice(1);

// "7850 lehigh ave, morton grove, il 60053" → "7850 Lehigh Ave, Morton Grove, IL 60053"
const formatAddress = (s = '') =>
  s
    .split(' ')
    .map((w) =>
      /^[a-z]{2},?$/i.test(w) && s.trim().endsWith(w) === false && /^[a-z]{2}$/i.test(w)
        ? w.toUpperCase()
        : capitalize(w.toLowerCase())
    )
    .join(' ')
    .replace(/, ([A-Za-z]{2}) (\d{5})/, (_, st, zip) => `, ${st.toUpperCase()} ${zip}`);

export default function FoundingWaitlist({ address, zipcode, onChangeAddress, onJoin }) {
  const [form, setForm] = useState({ name: '', phone: '', unit: '', smsConsent: true });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [done, setDone] = useState(null); // { firstName, position, alreadyRegistered, registeredAddress }

  const update = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: '' }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const phone = form.phone.replace(/\D/g, '');

    const next = {};
    if (name.length < 2) next.name = 'Enter your name.';
    if (phone.length !== 10) next.phone = 'Enter a 10-digit mobile number.';
    setErrors(next);
    if (Object.keys(next).length) return;

    setLoading(true);
    setSubmitError('');
    document.activeElement?.blur?.();

    try {
      const res = await onJoin?.({
        name,
        phone,
        unit: form.unit.trim(),
        address,
        zipcode,
        smsConsent: form.smsConsent,
        source: new URLSearchParams(window.location.search).get('src') || 'web',
      });

      // success: false → stay on the form and show an error
      if (!res?.success) {
        setSubmitError("We couldn't save your spot. Check your details and try again.");
        return;
      }

      const customer = res.customer || {};
      const savedName = customer.full_name || name;

      setDone({
        firstName: capitalize(savedName.trim().split(' ')[0]),
        position: res.position,
        alreadyRegistered: !!res.alreadyRegistered,
        registeredAddress: formatAddress(customer.Geolocation?.formattedAddress || address || ''),
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      setSubmitError("We couldn't save your spot. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ── Success ── */
  if (done) {
    return (
      <section className='fw'>
        <div className='fw-panel fw-done'>
          <div className='fw-check' aria-hidden='true'>
            <svg viewBox='0 0 52 52'>
              <circle className='fw-checkCircle' cx='26' cy='26' r='24' />
              <path className='fw-checkMark' d='M15 27l7 7 15-16' />
            </svg>
          </div>
          <h2 className='fw-title'>Your spot is reserved, {done.firstName}</h2>
          <p className='fw-sub'>
            Your $20 OFF and FREE Juice perks are now linked to your phone number! We'll text you as
            soon as delivery becomes available at your address.
          </p>

          {done.alreadyRegistered && (
            <div className='fw-member' role='status'>
              <div className='fw-memberTop'>
                <span className='fw-memberBadge'>
                  <svg viewBox='0 0 24 24' aria-hidden='true'>
                    <path d='M12 2.5l2.9 5.9 6.5.9-4.7 4.6 1.1 6.5L12 17.3l-5.8 3.1 1.1-6.5L2.6 9.3l6.5-.9z' />
                  </svg>
                  Founding member
                </span>
                <span className='fw-memberStatus'>Active</span>
              </div>

              <p className='fw-memberText'>
                You're already on our founding member waitlist. Your perks are saved.
              </p>

              {done.registeredAddress && (
                <div className='fw-memberAddr'>
                  <svg viewBox='0 0 24 24' aria-hidden='true'>
                    <path d='M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z' />
                  </svg>
                  <div>
                    <span className='fw-memberLabel'>Registered address</span>
                    <span className='fw-memberAddrText'>{done.registeredAddress}</span>
                  </div>
                </div>
              )}
            </div>
          )}
          {done.position ? (
            <div className='fw-number'>
              Founding member
              <strong>No. {String(done.position).padStart(4, '0')}</strong>
            </div>
          ) : null}
        </div>
      </section>
    );
  }

  /* ── Form ── */
  return (
    <section className='fw'>
      <div className='fw-panel'>
        <div className='fw-where'>
          <span className='fw-whereText'>
            <svg className='fw-pin' viewBox='0 0 24 24' aria-hidden='true'>
              <path d='M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z' />
            </svg>
            <b>{address}</b>
          </span>
          {onChangeAddress && (
            <button type='button' className='fw-link' onClick={onChangeAddress}>
              Change
            </button>
          )}
        </div>

        <span className='fw-status'>Launching soon</span>
        <h2 className='fw-title'>You're in our founding area</h2>
        <p className='fw-sub'>
          We haven't started delivering here yet. Reserve a founding member spot and lock in these
          perks.
        </p>

        <ul className='fw-perks'>
          {PERKS.map((p) => (
            <li className='fw-perk' key={p.title}>
              <span className={`fw-fig${p.small ? ' fw-fig--sm' : ''}`}>{p.fig}</span>
              <div>
                <h3>{p.title}</h3>
                <p>{p.text}</p>
              </div>
            </li>
          ))}
        </ul>

        <form className='fw-form' onSubmit={onSubmit} noValidate>
          <div className={`fw-field${errors.name ? ' is-invalid' : ''}`}>
            <label htmlFor='fw-name'>Full name</label>
            <input
              id='fw-name'
              type='text'
              autoComplete='name'
              autoCapitalize='words'
              enterKeyHint='next'
              placeholder='Jordan Lee'
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
            />
            {errors.name && <p className='fw-err'>{errors.name}</p>}
          </div>

          <div className='fw-row'>
            <div className={`fw-field${errors.phone ? ' is-invalid' : ''}`}>
              <label htmlFor='fw-phone'>Mobile number</label>
              <input
                id='fw-phone'
                type='tel'
                inputMode='tel'
                autoComplete='tel-national'
                enterKeyHint='next'
                placeholder='(312) 555-0142'
                value={form.phone}
                onChange={(e) => update('phone', formatPhone(e.target.value))}
              />
              {errors.phone && <p className='fw-err'>{errors.phone}</p>}
            </div>

            <div className='fw-field fw-field--unit'>
              <label htmlFor='fw-unit'>
                Apt <span className='fw-opt'>(optional)</span>
              </label>
              <input
                id='fw-unit'
                type='text'
                autoComplete='address-line2'
                enterKeyHint='done'
                placeholder='2B'
                value={form.unit}
                onChange={(e) => update('unit', e.target.value)}
              />
            </div>
          </div>

          <label className='fw-consent'>
            <input
              type='checkbox'
              checked={form.smsConsent}
              onChange={(e) => update('smsConsent', e.target.checked)}
            />
            <span>
              Text me launch updates and my founder perks. Msg &amp; data rates may apply. Reply
              STOP to opt out.
            </span>
          </label>

          <button className='fw-btn' type='submit' disabled={loading}>
            {loading && <span className='fw-spinner' aria-hidden='true' />}
            {loading ? 'Saving…' : 'Reserve my founder spot'}
          </button>

          {submitError && (
            <p className='fw-err fw-err--center' role='alert'>
              {submitError}
            </p>
          )}
          <p className='fw-fine'>Free to join. No payment needed.</p>
        </form>
      </div>

      <p className='fw-terms'>
        $20 off applies to the first order only. Free juice applies to orders over $30 before tax
        and fees. Perks are tied to the phone number used at signup.
      </p>
    </section>
  );
}
